import _ from "lodash";
import moment from "moment";
import { storage } from "../../config/firebase";
import { deleteObject, ref } from "firebase/storage";
import SupabaseDocument from "data/supabase/document";
import { supabase } from "config/supabase";
import AnalyticsService from "./analytics";
import TimeUtils from "utils/time";
import { FIREBASE_CONFIG } from "../../config/app";

export default class Image extends SupabaseDocument {
  static COLLECTION = "images";

  constructor(id = null, data = {}) {
    super();

    this.id = id;
    this.uid = _.get(data, "uid", null);

    this.name = _.get(data, "name", null);
    this.date = _.get(data, "date", moment().format(TimeUtils.FullDateString));
    this.caption = _.get(data, "caption", "");
    this.album = _.get(data, "album", "Uncategorized");
    this.favorited = _.get(data, "favorited", false);
    this.uploaded = _.get(data, "uploaded", null);
    this.folder = _.get(data, "folder", "users");
    // Natural pixel dimensions (captured at upload) so the feed can reserve the
    // right aspect ratio before the blob decodes. Null when unknown (older rows).
    this.width = _.get(data, "width", null);
    this.height = _.get(data, "height", null);
    // Byte size of the stored blob (captured at upload) for per-user storage
    // accounting. Null when unknown (older rows, backfilled from Storage).
    this.bytes = _.get(data, "bytes", null);

    this.saveCheckpoint();
  }

  // Build an Image from a Supabase `images` row. Blobs stay in Firebase Storage.
  static fromRow(row) {
    if (!row) return null;
    return new Image(row.id, {
      uid: row.uid,
      name: row.name,
      date: row.taken_at,
      caption: row.caption,
      album: row.album,
      favorited: row.favorited,
      uploaded: row.created_at,
      folder: row.folder || "users",
      width: row.width,
      height: row.height,
      bytes: row.bytes,
    });
  }

  // snake_case row written to Supabase. id + uid are added by the base save().
  // `link` mirrors the Firebase Storage URL; `uploaded` maps to created_at (DB default).
  toRow() {
    return {
      name: this.name,
      taken_at: this.date ? moment(this.date).toISOString() : null,
      caption: _.trim(this.caption),
      album: _.trim(this.album),
      favorited: this.favorited,
      folder: this.folder,
      link: this.url,
      width: this.width,
      height: this.height,
      bytes: this.bytes,
    };
  }

  async delete() {
    if (this.id) {
      AnalyticsService.event("delete_progress_photo");

      // Delete storage object first, ignoring "not found" errors
      // (e.g. if storage was already deleted but the row remained)
      try {
        const imageRef = ref(storage, this.location);
        if (imageRef) {
          await deleteObject(imageRef);
        }
      } catch (e) {
        if (e?.code !== "storage/object-not-found") {
          throw e;
        }
      }

      // Always delete the Supabase row
      return super.delete();
    }
  }

  get displayName() {
    return _.replace(this.name, /^[0-9]+\s/, "");
  }

  get json() {
    const data = {
      uid: this.uid,

      name: this.name,
      date: moment(this.date).format(TimeUtils.FullDateString),
      dateUTC: moment(this.date).utc().toISOString(),
      caption: _.trim(this.caption),
      album: _.trim(this.album),
      favorited: this.favorited,
    };

    // Set uploaded timestamp only on first save (new images)
    if (!this.uploaded) {
      data.uploaded = moment().utc().toISOString();
    }

    return data;
  }

  // Recent public photos for the feed. Reads the `public_images` view, which only
  // exposes images from public, non-incognito, non-deleted users -- that view is
  // the privacy gate. `favorited` is intentionally NOT filtered: it only controls
  // whether an image shows on the owner's own progress page, not the feed.
  static async getRecent(limitCount = 5, lastDoc = null) {
    const todayStart = moment().startOf("day").utc().toISOString();
    const key = `global_recent_${lastDoc ? lastDoc.id : "head"}`;
    const rows = await SupabaseDocument.query(
      "public_images",
      (q) => {
        let query = q
          .gte("created_at", todayStart)
          .order("created_at", { ascending: false });
        // keyset pagination on the ordered column
        if (lastDoc?.created_at) {
          query = query.lt("created_at", lastDoc.created_at);
        }
        return query.limit(limitCount);
      },
      key
    );

    return {
      images: rows.map((r) => Image.fromRow(r)),
      lastVisible: _.last(rows) || null, // raw row: carries id + created_at cursor
    };
  }

  // Rename an album in one shot: move every image under `oldName` to `newName`,
  // instead of making the user edit each photo. Returns the number of images
  // moved. Renaming onto an existing album name simply merges the two.
  static async renameAlbum(uid, oldName, newName) {
    const from = _.trim(oldName);
    const to = _.trim(newName);
    if (!to || to === from) return 0;

    const { data, error } = await supabase
      .from(Image.COLLECTION)
      .update({ album: to })
      .eq("uid", uid)
      .eq("album", from)
      .select("id");
    if (error) throw error;

    // This bypasses save(), so clear the query cache ourselves -- otherwise the
    // next list() returns the pre-rename rows until a reload.
    SupabaseDocument.resetCache();
    AnalyticsService.event("rename_progress_album");
    return (data || []).length;
  }

  static async list(uid, starredOnly = true) {
    const rows = await SupabaseDocument.query(
      Image.COLLECTION,
      (q) => {
        let query = q.eq("uid", uid);
        if (starredOnly) query = query.eq("favorited", true);
        return query;
      },
      `for ${uid} ${starredOnly ? "starred" : "all"}`
    );
    return rows.map((r) => Image.fromRow(r));
  }

  get location() {
    if (this.folder === "profile") {
      return `profile/${this.uid}`;
    }
    return `users/${this.uid}/${this.name}`;
  }

  get month() {
    return moment(this.date).startOf("month").format(TimeUtils.FullDateString);
  }

  sameMonthAndYear(comparison) {
    return this.month === comparison.format(TimeUtils.FullDateString);
  }

  async save() {
    const original_json = _.cloneDeep(this.json_original);
    return super.save().then(() => {
      if (this.date !== original_json.date) {
        AnalyticsService.event("update_progress_photo_date");
      }
      if (this.caption !== original_json.caption) {
        AnalyticsService.event("update_progress_photo_caption");
      }
      if (this.album !== original_json.album) {
        AnalyticsService.event("update_progress_photo_album");
      }
      if (this.favorited !== original_json.favorited) {
        AnalyticsService.event("update_progress_photo_favorited", {
          favorite: this.favorited,
        });
      }
    });
  }

  toggleFavorited() {
    this.favorited = !this.favorited;
  }

  get url() {
    const uidFragment = this.uid ? `%2F${this.uid}` : "";
    return `https://firebasestorage.googleapis.com/v0/b/${
      FIREBASE_CONFIG.storageBucket
    }/o/${this.folder}${uidFragment}%2F${encodeURIComponent(
      this.name
    )}?alt=media`;
  }
}
